import { ScoreUtils, useScoreStore } from '@assets/model/ScoreUtils';
import { useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import React, { PureComponent, useRef, useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
    Cell
} from 'recharts';

import * as style from './ChartStyles.module.less';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { ChartUtils } from './ChartShared';
import { useNavUtils } from '@assets/utils/NavUtils';
import useMeasure from 'react-use-measure';

import { duration } from 'moment';

interface IChartCompareCompanies {
    companies: string[];
    reportId: string;
}

export const ChartCompareCompanies = observer((p: IChartCompareCompanies) => {
    const store = useStore();
    const nav = useNavUtils();
    const [ref, bounds] = useMeasure();
    const [clickedEntry, setClickedEntry] = React.useState({ id: '' });

    const scoreStore = useScoreStore();
    var groups = scoreStore.SCORE_FIELDS_GROUPS;
    var data = [];

    var companies = [p.reportId, ...p.companies];
    companies.forEach(reportId => {
        var companyEntry = {};
        var q = store.createDataAccess(reportId);
        if (q == null) return;

        companyEntry['id'] = reportId;
        companyEntry['name'] = q.value('company_name');
        companyEntry['Score'] = q.score();

        companyEntry['logo'] = q.value('logoURL');

        if (companyEntry['Score'] == -1) {
            return;
        }
        data.push(companyEntry);
    });

    const renderCustomAxisTick = ({ x, y, payload, visibleTicksCount }) => {
        const logoURL = data[payload.index].logo;
        const barWidth = Math.floor((bounds.width / (data.length + 1)) * 0.6); // Approximate way if chart width is known
        const alternateHeight = barWidth < 150;
        var textOffset = 0;
        if (alternateHeight) {
            if (payload.index % 2 == 0) {
                textOffset = -7;
            } else {
                textOffset = 7;
            }
        }

        const imageWidth = barWidth;
        const imageHeight = barWidth * 0.75;

        var img: any = (
            <image
                opacity={1}
                href={logoURL} // Replace with your image URL
                width={barWidth} // Adjust width
                height={barWidth * 0.75} // Adjust height
                preserveAspectRatio="xMidYMax meet"
            />
        );

        return (
            <g transform={`translate(${x},${y})`}>
                {
                    <svg
                        x={-barWidth / 2}
                        y={-barWidth - 25}
                        width={imageWidth}
                        height={imageHeight}
                    >
                        {img}
                    </svg>
                }

                <text
                    fontSize="12" // ✅ Set readable font size
                    fontFamily="LustTextMedium" // ✅ Keep it clean
                    x={0}
                    y={20 - textOffset}
                    textAnchor="middle"
                    fill="#212529"
                >
                    {payload.value}
                </text>
            </g>
        );
    };

    return (
        <>
            <Flex direction="column" fillCell={true}>
                <div
                    ref={ref}
                    style={{
                        height: '100%',
                        width: '100%',
                        position: 'absolute',
                        opacity: 0
                    }}
                ></div>

                <div className={style.title}>Benchmarking</div>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        width={500}
                        height={300}
                        data={data}
                        margin={{
                            top: 20,
                            left: 20,
                            right: 20,
                            bottom: 20
                        }}
                    >
                        <defs>{ChartUtils.getBarGradient()}</defs>

                        <CartesianGrid strokeDasharray="3 3" />
                        <YAxis />
                        <Tooltip />

                        <Bar
                            dataKey={'Score'}
                            stackId="a"
                            radius={[10, 10, 0, 0]}
                        >
                            {data.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={
                                        entry.id === p.reportId
                                            ? 'url(#barGradientGold)'
                                            : 'url(#barGradientBlue)'
                                    }
                                    stroke={
                                        clickedEntry.id === entry.id
                                            ? '#212529'
                                            : 'none'
                                    }
                                    strokeWidth={2}
                                    style={{
                                        filter: ChartUtils.SHADOW_FILTER
                                    }}
                                    onClick={() => {
                                        if (entry.id == p.reportId) return;
                                        setClickedEntry({ id: entry.id });
                                        setTimeout(() => {
                                            setClickedEntry({ id: '' });
                                            nav.toReport(entry.id);
                                        }, 5);
                                    }}
                                />
                            ))}
                            <LabelList
                                dataKey="Score"
                                position="insideBottom"
                                formatter={v => v + '%'}
                                offset={20}
                                fill="white"
                                stroke="transparent"
                            />
                        </Bar>
                        <XAxis
                            dataKey="name"
                            tick={renderCustomAxisTick}
                            interval={0}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </Flex>
        </>
    );
});
